import { createUseStyles } from "@gfg/ui/theming";

import { LinearProgress } from "@gfg/ui/components/linear-progress";
import { useNavigate, useParams } from "react-router-dom";
import { SearchOptimization, SearchOptimizationState } from "@coral/typings";
import { useCallback } from "react";
import OptimizationsTable from "search-optimizations/components/optimizations-table";
import TableHeader from "../../../common/components/table/table-header";
import useGetSearchOptimizations from "~/search-optimizations/hooks/use-get-search-optimizations";
import useDeleteSearchOptimization from "~/search-optimizations/hooks/use-delete-search-optimization";
import useSubmit from "./use-submit";

const useStyles = createUseStyles(theme => ({
  tableContainer: {
    "boxShadow": theme.shadow("md"),
    "margin": `${theme.spacing(16)}`,
    "& .tableBody tr": {
      borderTopColor: "transparent",
    },
  },
  headerCell: {
    color: theme.colors.black,
    background: theme.colors.white,
  },
}));

export default function SearchOptimizationsPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { region } = useParams();

  const deleteSearchOptimization = useDeleteSearchOptimization();
  const submitStateChange = useSubmit();

  const {
    searchOptimizations,
    loading,
    limit,
    setLimit,
    stateFilter,
    setStateFilter,
    offset,
    setOffset,
    searchInput,
    setSearchInput,
  } = useGetSearchOptimizations();

  const handleCreateOptimization = useCallback(() => {
    navigate(`/${region}/search/optimizations/create`);
  }, [navigate]);

  const handleEditOptimization = useCallback(
    (optimization: SearchOptimization) => {
      navigate(`/${region}/search/optimizations/edit/${optimization.id}`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    async (optimization: SearchOptimization) => {
      await deleteSearchOptimization(optimization.id, optimization.query);
    },
    [deleteSearchOptimization],
  );

  const handleStateChange = useCallback(
    async (optimization: SearchOptimization, newState: SearchOptimizationState) => {
      await submitStateChange(optimization.id, newState);
    },
    [submitStateChange],
  );

  return (
    <div>
      <div className={classes.tableContainer}>
        <TableHeader
          total={searchOptimizations?.total}
          onSearchInputChange={setSearchInput}
          searchInput={searchInput}
          subtitle="Search Optimization"
          searchInputPlaceholder="Search"
          actionButtonLabel="Create optimization"
          onActionButtonClick={handleCreateOptimization}
        />
        {loading && <LinearProgress indeterminate />}
        <OptimizationsTable
          limit={limit}
          offset={offset}
          onEditOptimization={handleEditOptimization}
          onRemoveOptimization={handleDelete}
          total={searchOptimizations?.total}
          onLimitChange={setLimit}
          onOffsetChange={setOffset}
          stateFilterValue={stateFilter}
          onStateFilterChange={setStateFilter}
          searchOptimizations={searchOptimizations?.data}
          onOptimizationStateChange={handleStateChange}
        />
      </div>
    </div>
  );
}
